import { buildCanonicalMapUrl, parseQuerySignalPatch } from "./map-query-state.js";
import {
  buildSearchExpressionStatePatch,
  normalizeFishFilterPrefixTerm,
  normalizeFishFilterTerm,
  normalizeRawSearchExpressionTree,
  parseSearchExpressionDslResult,
} from "./map-search-contract.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function trimString(value) {
  return String(value ?? "").trim();
}

function mergeProjectionPatch(target, patch, prefix = "") {
  if (!isPlainObject(target) || !isPlainObject(patch)) {
    return target;
  }
  for (const [key, value] of Object.entries(patch)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (path === "_map_ui.search.expression") {
      target[key] = cloneJson(value);
      continue;
    }
    if (Array.isArray(value)) {
      target[key] = cloneJson(value);
      continue;
    }
    if (isPlainObject(value)) {
      const nextTarget = isPlainObject(target[key]) ? target[key] : {};
      target[key] = nextTarget;
      mergeProjectionPatch(nextTarget, value, path);
      continue;
    }
    target[key] = value;
  }
  return target;
}

function currentLocationHref(globalRef = globalThis) {
  return globalRef.location?.href || globalRef.window?.location?.href || "";
}

function replaceCanonicalUrlForProjectionPatch(
  projection,
  patch,
  globalRef = globalThis,
) {
  if (patch?._map_ui?.search?.expression == null) {
    return false;
  }
  const current = assertMapShellProjectionV1(projection);
  const href = currentLocationHref(globalRef);
  const history = globalRef.history || globalRef.window?.history;
  if (!href || typeof history?.replaceState !== "function") {
    return false;
  }
  const nextSignals = { _map_ui: cloneJson(current.ui) };
  mergeProjectionPatch(nextSignals, patch);
  let currentUrl;
  let nextUrl;
  try {
    currentUrl = new URL(href, "https://fishystuff.fish").toString();
    nextUrl = buildCanonicalMapUrl(nextSignals, { baseHref: href });
  } catch (_error) {
    return false;
  }
  if (!nextUrl || nextUrl === currentUrl) {
    return false;
  }
  history.replaceState(history.state ?? null, "", nextUrl);
  return true;
}

function normalizeSearchExpressionIssues(issues) {
  return Array.isArray(issues)
    ? issues
        .filter((issue) => isPlainObject(issue))
        .map((issue) => cloneJson(issue))
    : [];
}

function searchIssuesHaveCatalogUnavailable(issues) {
  return normalizeSearchExpressionIssues(issues).some((issue) =>
    issue.stage === "resolve" && issue.code === "catalog_unavailable"
  );
}

function rawExpressionCatalogRequirements(node, requirements = null) {
  const next = requirements || {
    fish: false,
    items: false,
    semanticTerms: false,
    unprefixedCatalogs: false,
  };
  const normalized = normalizeRawSearchExpressionTree(node);
  if (!normalized) {
    return next;
  }
  if (normalized.type === "group") {
    for (const child of normalized.children) {
      rawExpressionCatalogRequirements(child, next);
    }
    return next;
  }
  if (normalized.type === "container") {
    return rawExpressionCatalogRequirements(normalized.expression, next);
  }
  const term = normalized.term;
  if (term?.kind === "prefixed" && term.prefix === "fish") {
    next.fish = true;
  } else if (
    (term?.kind === "prefixed" && term.prefix === "item") ||
    (term?.kind === "prefixed-list" && term.prefix === "items")
  ) {
    next.items = true;
  } else if (term?.kind === "semantic") {
    next.semanticTerms = true;
  } else if (
    term?.kind === "bare" &&
    !normalizeFishFilterTerm(term.value) &&
    !normalizeFishFilterPrefixTerm(term.value)
  ) {
    next.unprefixedCatalogs = true;
  }
  return next;
}

function rawExpressionCatalogsAvailable(catalog, catalogRevision, rawExpressionTree) {
  const requirements = rawExpressionCatalogRequirements(rawExpressionTree);
  const hasCompleteUnprefixedCatalog = [
    catalog?.fish,
    catalog?.items,
    catalog?.semanticTerms,
  ].every((entries) => Array.isArray(entries) && entries.length > 0);
  return (
    (!requirements.unprefixedCatalogs ||
      (Number.isSafeInteger(catalogRevision) &&
        catalogRevision > 0 &&
        hasCompleteUnprefixedCatalog)) &&
    (!requirements.fish || (Array.isArray(catalog?.fish) && catalog.fish.length > 0)) &&
    (!requirements.items || (Array.isArray(catalog?.items) && catalog.items.length > 0)) &&
    (!requirements.semanticTerms ||
      (Array.isArray(catalog?.semanticTerms) && catalog.semanticTerms.length > 0))
  );
}

function searchIssueSignature(issues) {
  return JSON.stringify(normalizeSearchExpressionIssues(issues));
}

export function buildQueryRawExpressionSignalPatch(projection) {
  const current = assertMapShellProjectionV1(projection);
  const search = isPlainObject(current.ui?.search) ? current.ui.search : {};
  const rawExpression = trimString(search.rawExpression);
  const rawExpressionTree = normalizeRawSearchExpressionTree(search.rawExpressionTree);
  if (!rawExpression || !rawExpressionTree || !searchIssuesHaveCatalogUnavailable(search.expressionIssues)) {
    return null;
  }

  const catalog = isPlainObject(current.runtime?.catalog)
    ? current.runtime.catalog
    : {};
  if (!rawExpressionCatalogsAvailable(
    catalog,
    current.runtime?.catalogRevision,
    rawExpressionTree,
  )) {
    return null;
  }

  const result = parseSearchExpressionDslResult(rawExpression, { catalog });
  const nextRawExpressionTree =
    normalizeRawSearchExpressionTree(result.rawExpressionTree) || rawExpressionTree;
  const nextIssues = normalizeSearchExpressionIssues(result.issues);
  const hasErrorIssue = nextIssues.some((issue) => issue.severity !== "warning");

  if (result.expression && !hasErrorIssue) {
    const patch = buildSearchExpressionStatePatch(result.expression);
    if (nextIssues.length) {
      patch._map_ui.search.rawExpression = rawExpression;
      patch._map_ui.search.rawExpressionTree = nextRawExpressionTree;
      patch._map_ui.search.expressionIssues = nextIssues;
    }
    return patch;
  }

  if (
    searchIssueSignature(search.expressionIssues) === searchIssueSignature(nextIssues) &&
    JSON.stringify(rawExpressionTree) === JSON.stringify(nextRawExpressionTree)
  ) {
    return null;
  }

  return {
    _map_ui: {
      search: {
        rawExpression,
        rawExpressionTree: nextRawExpressionTree,
        expressionIssues: nextIssues,
      },
    },
  };
}

export function createMapPageDerivedController({
  globalRef = globalThis,
  readProjection,
  subscribeProjection,
  dispatchPatch = () => false,
} = {}) {
  if (typeof readProjection !== "function") {
    throw new TypeError("Map page derived state requires a projection reader");
  }
  if (typeof subscribeProjection !== "function") {
    throw new TypeError("Map page derived state requires a projection subscription");
  }

  const state = {
    started: false,
    receivedInitialProjection: false,
    unsubscribe: null,
    observedSearch: null,
    observedCatalog: null,
    observedCatalogRevision: null,
  };

  function resolveRawExpression(projection) {
    const rawExpressionPatch = buildQueryRawExpressionSignalPatch(projection);
    if (rawExpressionPatch) {
      const accepted = dispatchPatch(rawExpressionPatch);
      if (
        accepted !== false
        && rawExpressionPatch?._map_ui?.search?.expression != null
      ) {
        replaceCanonicalUrlForProjectionPatch(
          projection,
          rawExpressionPatch,
          globalRef,
        );
      }
    }
    return rawExpressionPatch;
  }

  function observeProjection(projection) {
    const current = assertMapShellProjectionV1(projection);
    state.receivedInitialProjection = true;
    const search = current.ui?.search;
    const catalog = current.runtime?.catalog;
    const catalogRevision = current.runtime?.catalogRevision;
    if (
      search === state.observedSearch
      && catalog === state.observedCatalog
      && catalogRevision === state.observedCatalogRevision
    ) {
      return false;
    }
    state.observedSearch = search;
    state.observedCatalog = catalog;
    state.observedCatalogRevision = catalogRevision;
    return Boolean(resolveRawExpression(current));
  }

  function handleSubscribedProjection(projection) {
    if (!state.started) {
      return false;
    }
    try {
      return observeProjection(projection);
    } catch (_error) {
      return false;
    }
  }

  function applyInitialPatches(locationHref = currentLocationHref(globalRef)) {
    const queryPatch = parseQuerySignalPatch(locationHref);
    if (queryPatch) {
      dispatchPatch(queryPatch);
    }
    const rawExpressionPatch = resolveRawExpression(
      assertMapShellProjectionV1(readProjection()),
    );
    return Object.freeze({
      queryPatch,
      rawExpressionPatch,
    });
  }

  function resetSubscriptionState() {
    const unsubscribe = state.unsubscribe;
    state.started = false;
    state.receivedInitialProjection = false;
    state.unsubscribe = null;
    state.observedSearch = null;
    state.observedCatalog = null;
    state.observedCatalogRevision = null;
    return unsubscribe;
  }

  function start() {
    if (state.started) {
      return true;
    }
    let unsubscribe = null;
    state.started = true;
    state.receivedInitialProjection = false;
    try {
      unsubscribe = subscribeProjection(handleSubscribedProjection, {
        emitCurrent: true,
      });
      if (typeof unsubscribe !== "function") {
        throw new TypeError(
          "Map page derived projection subscription must return an unsubscribe function",
        );
      }
      state.unsubscribe = unsubscribe;
      if (!state.receivedInitialProjection) {
        const stopSubscription = resetSubscriptionState();
        stopSubscription?.();
        return false;
      }
      return true;
    } catch (error) {
      const stopSubscription = state.unsubscribe || unsubscribe;
      resetSubscriptionState();
      stopSubscription?.();
      throw error;
    }
  }

  function stop() {
    if (!state.started) {
      return;
    }
    const unsubscribe = resetSubscriptionState();
    unsubscribe?.();
  }

  return Object.freeze({
    applyInitialPatches,
    start,
    stop,
  });
}
